import type { MerchantExternalFooterItem, NavigationItemDto } from '@accesso/cc-ecomm-api';
import type { MenuItem, SupportedLanguage } from '@accesso/cc-std-lib';
import type { IconLink, NavLink } from '@accesso/cc-ui';
import type { LocaleModel, MerchantModel, StoreModel } from '@accesso/cc-ui/model';
import type { VerticalNavigationSocialHandles } from '@accesso/ng-uds/vertical-navigation';
import { isPlatformBrowser, PlatformLocation } from '@angular/common';
import { HttpStatusCode } from '@angular/common/http';
import { computed, inject, PLATFORM_ID } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';

import { isNonEmptyArray, WINDOW } from '@accesso/cc-std-lib';
import { UdsBrandIcon } from '@accesso/uds-icons/mjs';

import { QUERY_PARAM_LANGUAGE } from '../../core/enums/query-params';
import { RedirectError } from '../../core/errors/redirect.error';

export interface MerchantState {
	initialized: boolean;
	store: StoreModel | null;
	merchant: MerchantModel | null;
	languageLocales: LocaleModel | null;
	selectedLanguage: SupportedLanguage;
}

const initialState: MerchantState = {
	initialized: false,
	store: null,
	merchant: null,
	languageLocales: null,
	selectedLanguage: 'en',
};

export const MerchantStore = signalStore(
	{ providedIn: 'root' },
	withState(initialState),
	withComputed(({ merchant, languageLocales }) => ({
		merchantLoaded: computed(() => merchant()?.state().loaded ?? false),
		languageLocaleLoaded: computed(() => languageLocales()?.state().loaded ?? false),
		media: computed(() => merchant()?.settings()?.media),
		items: computed(() => merchant()?.navigation()?.reduce(reduceNavigationItemsToMenuItem, []) ?? []),
		links: computed<MerchantExternalFooterItem[]>(() => merchant()?.settings()?.footerSettings?.items ?? []),
		socialHandles: computed<VerticalNavigationSocialHandles | undefined>(
			() => merchant()?.settings()?.footerSettings?.socialHandles as VerticalNavigationSocialHandles
		),
		hasMenu: computed(() => {
			const merchantValue = merchant();
			if (!merchantValue) return false;
			return (
				merchantValue.navigation()?.length > 0 ||
				!!merchantValue.settings()?.footerSettings?.socialHandles ||
				(merchantValue.settings()?.footerSettings?.items?.length ?? 0) > 0 ||
				(merchantValue.settings()?.supportedLanguages?.length ?? 0) > 1
			);
		}),
		footerLinks: computed<NavLink[]>(
			() =>
				merchant()
					?.settings()
					?.footerSettings?.items?.map(({ label, url }) => ({ label, url })) ?? []
		),
		contact: computed(() => merchant()?.settings()?.contact),
		footerSocialLinks: computed(() => getFooterSocialLinks(merchant()?.settings()?.footerSettings?.socialHandles)),
		supportedLanguages: computed(() => {
			if (!merchant() || !languageLocales()) return [];

			const { supportedLanguages } = merchant()?.settings() ?? {};
			if (!isNonEmptyArray(supportedLanguages)) return [];

			const attributes = languageLocales()?.attributes() as Record<string, SupportedLanguage>;

			return supportedLanguages.reduce((acc: { label: string; value: SupportedLanguage }[], { lang, active }) => {
				const value = lang as SupportedLanguage;
				if (active && attributes && attributes[lang]?.length) {
					acc.push({ label: attributes[lang], value });
				}

				return acc;
			}, []);
		}),
		supportedLanguagesList: computed(() => {
			if (!merchant() || !languageLocales()) return [];
			return (
				merchant()
					?.settings()
					?.supportedLanguages?.filter(({ active }) => active)
					.map(({ lang }) => lang as SupportedLanguage) ?? []
			);
		}),
		defaultLanguage: computed(() => merchant()?.settings().defaultLanguage ?? 'en'),
	})),
	withMethods((store, window = inject(WINDOW), platformId = inject(PLATFORM_ID), location = inject(PlatformLocation)) => ({
		initialize(merchant: MerchantModel, storeModel: StoreModel, language: string, languageLocales: LocaleModel): void {
			patchState(store, { initialized: true, merchant, store: storeModel, selectedLanguage: language as SupportedLanguage, languageLocales });
		},
		setSelectedLanguage(lang: string | SupportedLanguage): void {
			const supportedLanguages = store.merchant()?.settings().supportedLanguages ?? [];
			const currentLang = store.selectedLanguage();
			if (!isSupportedLanguage(supportedLanguages, lang) || currentLang === lang) {
				return;
			}

			if (currentLang === lang) return; // No change needed
			const url = new URL(location.href);
			const searchParams = new URLSearchParams(url.search);

			if (store.defaultLanguage() === lang) {
				searchParams.delete(QUERY_PARAM_LANGUAGE);
			} else {
				searchParams.set(QUERY_PARAM_LANGUAGE, lang as string);
			}

			url.search = searchParams.toString();

			patchState(store, { selectedLanguage: lang as SupportedLanguage });

			if (isPlatformBrowser(platformId)) {
				window.location.href = url.toString(); // This will cause a full page reload
			} else {
				throw new RedirectError(location.href, url.toString(), HttpStatusCode.TemporaryRedirect);
			}
		},
	}))
);

export function reduceNavigationItemsToMenuItem(
	acc: MenuItem[],
	{ id, label, value, active, type, homepage, homepageAsTab, items }: NavigationItemDto
): MenuItem[] {
	if (!active || (type === 'group' && !items?.length) || (type === 'group' && items?.every(({ active }) => !active)) || (homepage && !homepageAsTab)) {
		return acc;
	}

	const item: MenuItem = {
		id,
		label,
		value,
		type,
		items: !items?.length ? [] : items.reduce(reduceNavigationItemsToMenuItem, []),
	};

	if (type === 'page-template') {
		item.routerLink = `page/${value}`;
		item.routerLinkActiveOptions = { exact: true };
	} else if (type === 'product') {
		item.routerLink = `product/${value}`;
		item.routerLinkActiveOptions = { matrixParams: 'subset', queryParams: 'ignored', paths: 'subset', fragment: 'ignored' };
	} else if (type === 'external') {
		item.url = value;
		item.urlTarget = '_blank';
	}

	acc.push(item);

	return acc;
}

export function isSupportedLanguage(supportedLanguages: { lang: string; active: boolean }[], lang: string): boolean {
	return supportedLanguages.some((l) => l.lang === lang && l.active);
}

export function getFooterSocialLinks(socialHandles?: VerticalNavigationSocialHandles): IconLink[] {
	if (!socialHandles) return [];

	return (<(keyof VerticalNavigationSocialHandles)[]>Object.keys(socialHandles)).reduce<IconLink[]>((acc, social) => {
		if (!socialHandles[social]) return acc;

		switch (social) {
			case 'facebook':
				acc.push({ label: 'Facebook', icon: UdsBrandIcon.FACEBOOK, url: `https://facebook.com/${socialHandles[social]}` });
				break;
			case 'instagram':
				acc.push({ label: 'Instagram', icon: UdsBrandIcon.INSTAGRAM, url: `https://instagram.com/${socialHandles[social]}` });
				break;
			case 'x':
				acc.push({ label: 'X', icon: UdsBrandIcon.X_TWITTER, url: `https://x.com/${socialHandles[social]}` });
				break;
			case 'youtube':
				acc.push({ label: 'YouTube', icon: UdsBrandIcon.YOUTUBE, url: `https://youtube.com/@${socialHandles[social]}` });
				break;
		}

		return acc;
	}, []);
}
